import CloseSharpIcon from '@mui/icons-material/CloseSharp';
import { Box, Typography } from '@mui/material';
import EnergyPlusSVG from 'assets/partners/EnergyPlus.svg';
import FileLogo from 'assets/file-icon.svg';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

export const IDF_DROPZONE_SIZE = 355;

type IdfFileDropzoneProps = {
  file: File | null;
  onFileChange: (files: File[]) => void;
  onClear: (e: MouseEvent) => void;
  disabled?: boolean;
  isFileMissing?: boolean;
  isLoading?: boolean;
  /** Stretch across the parent instead of the square labs tile. */
  fullWidth?: boolean;
  /** Existing revision IDF name when no new File is selected (edit mode). */
  existingFileName?: string;
};

const IdfFileDropzone = ({
  file,
  onFileChange,
  onClear,
  disabled = false,
  isFileMissing = false,
  isLoading = false,
  fullWidth = false,
  existingFileName = '',
}: IdfFileDropzoneProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const displayName = file?.name || existingFileName;

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted[0]) onFileChange(accepted);
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: disabled || isLoading,
    // Filename-only check: browsers often give empty/odd MIME for .idf
    validator: (f) => {
      if (!/\.idf$/i.test(f.name)) {
        return { code: 'invalid-file', message: t('REVISION_FILE_INVALID_ERROR') };
      }
      return null;
    },
    onDropRejected: () => {
      enqueueSnackbar(t('REVISION_FILE_INVALID_ERROR'), { variant: 'error' });
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        width: '100%',
        maxWidth: fullWidth ? '100%' : { xs: '100%', lg: IDF_DROPZONE_SIZE },
        height: fullWidth ? 300 : { xs: 220, lg: IDF_DROPZONE_SIZE },
        minWidth: fullWidth ? 0 : { lg: IDF_DROPZONE_SIZE },
        minHeight: fullWidth ? 300 : { xs: 200, lg: IDF_DROPZONE_SIZE },
        maxHeight: fullWidth ? 300 : { lg: IDF_DROPZONE_SIZE },
        aspectRatio: fullWidth ? 'auto' : { lg: '1 / 1' },
        flex: { xs: 'none', lg: '0 0 auto' },
        alignSelf: 'stretch',
        cursor: disabled || isLoading ? 'default' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 2,
        borderRadius: '8px',
        border: `2px dashed ${isFileMissing ? '#e11d48' : isDragActive ? '#0E66F0' : '#cbd5e1'}`,
        background: isDragActive ? '#eef5fb' : '#f8fafc',
        transition: 'border 0.2s ease, background 0.2s ease',
        position: 'relative',
      }}
    >
      <input {...getInputProps()} />
      {isLoading ? (
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-[4.5rem] w-[4.5rem] animate-spin rounded-full border-[3px] border-[#0E66F0] border-t-transparent"
            aria-hidden
          />
          <span className="text-sm font-bold text-slate-700">
            {t('REVISION_ADD_LOADING')}
          </span>
        </div>
      ) : displayName ? (
        <>
          <Box
            component="img"
            src={FileLogo}
            alt="file"
            sx={{ width: 108, height: 'auto', mb: 1.5, pointerEvents: 'none' }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              color: '#0e66f0',
              textAlign: 'center',
              px: 1.5,
              wordBreak: 'break-word',
              fontSize: '1rem',
            }}
            dir="ltr"
          >
            {displayName}
          </Typography>
          <CloseSharpIcon
            onClick={onClear}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              cursor: 'pointer',
              color: '#000',
              border: '1.5px solid #000',
              borderRadius: '50%',
              p: '2px',
              fontSize: 20,
            }}
          />
        </>
      ) : (
        <>
          <Box
            component="img"
            src={EnergyPlusSVG}
            alt="EnergyPlus"
            sx={{ width: 126, height: 'auto', pointerEvents: 'none' }}
          />
          <Box
            sx={{
              mt: '15px',
              fontSize: '20px',
              textAlign: 'center',
              color: '#bdbdbd',
            }}
          >
            {t('REVISIONS_DROP_FILE_PLACEHOLDER')}
          </Box>
        </>
      )}
    </Box>
  );
};

export default IdfFileDropzone;
